import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Circle, Lock, RefreshCw, FileSpreadsheet, Eye, Calendar, Archive, FileCode2 } from "lucide-react";
import * as XLSX from "xlsx";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { downloadTallyXml } from "@/lib/tallyXml";
import PurgeDataButton from "@/components/PurgeDataButton";

const today = () => new Date().toISOString().slice(0, 10);
const money = (n) => `₹ ${Number(n || 0).toFixed(2)}`;

export default function AccountsBillsReview() {
  const { branch, user } = useAuth();
  const [date, setDate] = useState(today());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [billTypeFilter, setBillTypeFilter] = useState("all"); // all | direct | courier

  const load = useCallback(async () => {
    if (!branch?.code) return;
    setLoading(true);
    try {
      const params = { branch_code: branch.code, date, include_archived: includeArchived, limit: 500 };
      if (billTypeFilter !== "all") params.bill_type = billTypeFilter;
      const { data } = await api.get("/direct-bills", { params });
      setRows(data);
    } catch (err) {
      toast.error(formatApiError(err, "Load failed"));
    } finally { setLoading(false); }
  }, [branch?.code, date, includeArchived, billTypeFilter]);

  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => {
    const total = rows.reduce((s, b) => s + Number(b.total || 0), 0);
    const tallyTotal = rows.filter(b => b.send_to_tally).reduce((s, b) => s + Number(b.total || 0), 0);
    const verifiedCount = rows.filter(b => b.verified).length;
    return { count: rows.length, total, tallyTotal, tallyCount: rows.filter(b => b.send_to_tally).length, verifiedCount };
  }, [rows]);

  const toggleTally = async (bill) => {
    try {
      const { data } = await api.patch(`/direct-bills/${bill.id}/tally`, { send_to_tally: !bill.send_to_tally });
      setRows((prev) => prev.map((b) => (b.id === bill.id ? data : b)));
    } catch (err) { toast.error(formatApiError(err, "Toggle failed")); }
  };

  const verify = async (bill) => {
    try {
      const { data } = await api.patch(`/direct-bills/${bill.id}/verify`);
      setRows((prev) => prev.map((b) => (b.id === bill.id ? data : b)));
      toast.success(`Bill ${bill.bill_no} verified`);
    } catch (err) { toast.error(formatApiError(err, "Verify failed")); }
  };

  const closeEOD = async () => {
    if (!window.confirm(`Close EOD for ${branch.name} on ${date}?\n\n• Tally-marked bills remain in this list (archived, still reprintable).\n• Un-marked bills are MOVED to the superadmin vault and removed from this dashboard.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/direct-bills/close-eod", { branch_code: branch.code, date });
      toast.success(`EOD closed · ${data.tally_archived || 0} kept in Tally list · ${data.vaulted || 0} moved to superadmin vault`);
      load();
    } catch (err) { toast.error(formatApiError(err, "Close EOD failed")); }
    finally { setBusy(false); }
  };

  const exportExcel = (scope = "all") => {
    const src = scope === "tally" ? rows.filter(b => b.send_to_tally) : rows;
    if (src.length === 0) { toast.error("No bills to export"); return; }
    const bills = src.map((b) => ({
      "Bill No": b.bill_no,
      "Date": new Date(b.generated_at).toLocaleString(),
      "Customer": b.customer_name,
      "UHID": b.mrn || "",
      "Phone": b.customer_phone || "",
      "Items": (b.items || []).length,
      "Subtotal": Number(b.subtotal || 0),
      "Discount": Number(b.discount || 0),
      "Total": Number(b.total || 0),
      "Paid": Number(b.paid ?? b.total ?? 0),
      "Payment Mode": b.payment_mode || "cash",
      "Send to Tally": b.send_to_tally ? "YES" : "",
      "Verified": b.verified ? "YES" : "",
      "Archived": b.archived ? "YES" : "",
      "Cashier": b.generated_by_name || b.generated_by || "",
      "Notes": b.notes || "",
    }));
    const lines = [];
    src.forEach((b) => (b.items || []).forEach((it) => lines.push({
      "Bill No": b.bill_no,
      "Date": new Date(b.generated_at).toLocaleString(),
      "Item": it.name,
      "SKU": it.sku || "",
      "Qty": it.qty,
      "Rate": Number(it.price || 0),
      "Amount": Number(it.qty || 0) * Number(it.price || 0),
      "Send to Tally": b.send_to_tally ? "YES" : "",
    })));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(bills), "Bills");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(lines), "Line items");
    const fname = `DirectBills_${scope}_${branch.code}_${date}.xlsx`;
    XLSX.writeFile(wb, fname);
    toast.success(`Exported ${bills.length} bill${bills.length === 1 ? "" : "s"} · ${fname}`);
  };

  const exportTallyXml = () => {
    const tally = rows.filter((b) => b.send_to_tally);
    if (tally.length === 0) { toast.error("No Tally-marked bills for this day"); return; }
    downloadTallyXml(tally, `Tally_${branch.code}_${date}.xml`, { company: `Badarinath Ayurveda ${branch.code}` });
    toast.success(`Tally XML with ${tally.length} voucher${tally.length === 1 ? "" : "s"} downloaded`);
  };

  const isSuper = user?.role === "superadmin";

  return (
    <div className="max-w-6xl mx-auto animate-fade-up" data-testid="accounts-bills-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] mb-3 ornament-dot">
            Accounts · Direct bills · {branch.name}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-forest mb-1">Accounts &amp; Tally review</h1>
          <p className="text-[#5C5652]">Verify each direct bill, toggle Tally ledger flag for GST-eligible bills, and archive the day.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#5C5652] mb-1 flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" strokeWidth={1.75} /> Date
            </label>
            <input type="date" data-testid="acc-date" value={date} onChange={(e) => setDate(e.target.value)}
              className="h-10 px-3 rounded-lg border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 text-sm" />
          </div>
          <button onClick={load} data-testid="acc-refresh" className="h-10 mt-5 px-4 rounded-lg border border-sand bg-white hover:bg-[#F9F6F0] text-sm font-semibold inline-flex items-center gap-2">
            <RefreshCw className="h-4 w-4" strokeWidth={1.75} /> Refresh
          </button>
          <div className="mt-5">
            <PurgeDataButton collection="direct_bills" label="direct bills" onDone={load} />
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-4 mb-5">
        <SummaryCard label="Bills" value={summary.count} />
        <SummaryCard label="Total collected" value={money(summary.total)} />
        <SummaryCard label={`Tally marked (${summary.tallyCount})`} value={money(summary.tallyTotal)} accent="forest" />
        <SummaryCard label="Verified" value={`${summary.verifiedCount} / ${summary.count}`} />
      </div>

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <div className="inline-flex rounded-md border border-sand overflow-hidden text-xs" data-testid="acc-billtype-tabs">
          {[{v:"all",l:"All bills"},{v:"direct",l:"Direct"},{v:"courier",l:"Courier"}].map((b) => (
            <button key={b.v} onClick={() => setBillTypeFilter(b.v)} data-testid={`acc-billtype-${b.v}`}
              className={`px-3 h-8 font-semibold ${billTypeFilter === b.v ? "bg-forest text-white" : "bg-white text-[#5C5652] hover:bg-[#F9F6F0]"} ${b.v !== "all" ? "border-l border-sand" : ""}`}>{b.l}</button>
          ))}
        </div>
        <label className="text-xs inline-flex items-center gap-2 cursor-pointer text-[#5C5652]">
          <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} data-testid="acc-include-archived" className="h-4 w-4 accent-[#1A4331]" />
          Include archived
        </label>
        <div className="flex-1"></div>
        <button onClick={() => exportExcel("tally")} data-testid="acc-export-tally"
          className="h-10 px-3 rounded-lg border border-sand text-sm font-semibold hover:bg-[#F9F6F0] inline-flex items-center gap-2">
          <FileSpreadsheet className="h-4 w-4" strokeWidth={1.75} /> Export Tally-only
        </button>
        <button onClick={exportTallyXml} data-testid="acc-export-tally-xml"
          className="h-10 px-3 rounded-lg border border-sand text-sm font-semibold hover:bg-[#F9F6F0] inline-flex items-center gap-2">
          <FileCode2 className="h-4 w-4" strokeWidth={1.75} /> Download Tally XML
        </button>
        <button onClick={() => exportExcel("all")} data-testid="acc-export-all"
          className="h-10 px-3 rounded-lg border border-sand text-sm font-semibold hover:bg-[#F9F6F0] inline-flex items-center gap-2">
          <FileSpreadsheet className="h-4 w-4" strokeWidth={1.75} /> Export all
        </button>
        <button onClick={closeEOD} disabled={busy || rows.length === 0} data-testid="acc-close-eod"
          className="h-10 px-3 rounded-lg bg-forest text-white text-sm font-semibold hover:bg-forest-hover inline-flex items-center gap-2 disabled:opacity-60">
          <Archive className="h-4 w-4" strokeWidth={1.75} /> Close EOD &amp; archive
        </button>
      </div>

      <div className="bg-white border border-sand rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="acc-bills-table">
            <thead className="bg-[#F9F6F0] text-[10px] uppercase tracking-wider text-[#5C5652]">
              <tr>
                <th className="text-left px-3 py-2">Bill no</th>
                <th className="text-left px-3 py-2">Time</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">UHID</th>
                <th className="text-right px-3 py-2">Total</th>
                <th className="text-center px-3 py-2">Tally ledger</th>
                <th className="text-center px-3 py-2">Verified</th>
                <th className="text-center px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8E3D9]">
              {loading ? (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-[#5C5652]">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-[#5C5652]">No direct bills for {date}.</td></tr>
              ) : rows.map((b) => (
                <tr key={b.id} data-testid={`acc-row-${b.bill_no}`} className={b.archived ? "opacity-60" : ""}>
                  <td className="px-3 py-2 font-mono text-xs text-forest">{b.bill_no}</td>
                  <td className="px-3 py-2 text-xs">{new Date(b.generated_at).toLocaleTimeString()}</td>
                  <td className="px-3 py-2">{b.customer_name}<br/><span className="text-[10px] text-[#5C5652]">{b.customer_phone || "—"} · {(b.items || []).length} item · <span className="uppercase font-bold tracking-wider text-[9px]">{b.bill_type || "direct"}</span></span><div className="text-[9px] text-[#5C5652] mt-0.5">{(b.payments || []).map(p => `${p.mode}: ₹${Number(p.amount).toFixed(0)}`).join(" · ") || b.payment_mode}</div></td>
                  <td className="px-3 py-2 font-mono text-xs">{b.mrn || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">{money(b.total)}</td>
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={() => toggleTally(b)}
                      disabled={b.archived}
                      data-testid={`acc-tally-${b.bill_no}`}
                      title={b.send_to_tally ? "Marked — click to unmark" : "Mark for Tally GST ledger"}
                      className={`inline-flex items-center gap-1 h-8 px-3 rounded-full text-[11px] font-bold uppercase tracking-wider transition-colors ${b.send_to_tally ? "bg-forest text-white" : "bg-[#F9F6F0] text-[#5C5652] hover:bg-[#E8E3D9]"} ${b.archived ? "cursor-not-allowed" : ""}`}
                    >
                      {b.send_to_tally ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
                      {b.send_to_tally ? "Yes" : "No"}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {b.verified ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-forest font-bold uppercase tracking-wider"><CheckCircle2 className="h-3.5 w-3.5" /> Yes</span>
                    ) : (
                      <button onClick={() => verify(b)} disabled={b.archived} data-testid={`acc-verify-${b.bill_no}`}
                        className="h-7 px-3 rounded-md border border-sand text-[11px] font-bold uppercase tracking-wider text-[#5C5652] hover:bg-[#F9F6F0] disabled:opacity-40">
                        Verify
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center text-[10px]">
                    {b.archived ? <span className="inline-flex items-center gap-1 text-[#5C5652]"><Lock className="h-3 w-3" /> Archived</span> : <span className="text-forest">Open</span>}
                  </td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-[#F9F6F0]">
                <tr className="border-t border-sand">
                  <td colSpan={4} className="px-3 py-2 text-right font-bold text-[10px] uppercase tracking-wider text-[#5C5652]">Day total</td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-forest">{money(summary.total)}</td>
                  <td colSpan={3} className="px-3 py-2 text-[10px] text-[#5C5652]">Of which Tally-marked: <span className="font-mono font-bold text-forest">{money(summary.tallyTotal)}</span></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div className="mt-4 text-[11px] text-[#5C5652]">
        {isSuper && <span className="inline-flex items-center gap-1 text-forest font-semibold mr-2"><Eye className="h-3 w-3" /> Superadmin</span>}
        Tally-marked bills stay flagged so accounts can pull them into Tally at any time. "Close EOD &amp; archive" locks the day's bills to prevent edits — they remain fully searchable and re-printable.
      </div>
    </div>
  );
}

function SummaryCard({ label, value, accent }) {
  return (
    <div className="bg-white border border-sand rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">{label}</div>
      <div className={`font-display text-2xl mt-1 ${accent === "forest" ? "text-forest" : "text-[#2A2624]"}`}>{value}</div>
    </div>
  );
}
